import { Button, Flex, Form, Input, Popover, Switch } from 'antd';
import { useEffect, useMemo, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ChannelInfo } from '@shared/api';
import { Text } from '@shared/ui/antd';
import { useFormSchemaUpdate } from '@modules/channels/hooks/use-form-schema-update';
import type { ChannelUpdateFormValues } from '@modules/channels/lib/types';
import { CHANNELS_MAX_FORM_LIMIT } from '@modules/channels/lib/constants';
import { AgentsSelector } from '@shared/ui/selector/agents-selector';
import { PresetSelector } from '../preset-selector/preset-selector';
import { useUpdateChannel } from '@modules/channels/hooks/use-update-channel';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { Routes, buildOrganizationRoute } from '@shared/constants/routes';
import { ChannelIntegrationsSection } from '../integrations-section/integrations-section';
import styles from '../channel-create/channel-create-form.module.scss';
import { copyToClipboard } from '@shared/utils/copy-to-clipboard';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import {
  channelFormDraftsActions,
  channelFormDraftsSelectors,
} from '@shared/redux/ducks/channel-form-drafts';

interface ChannelUpdateFormProps {
  channel: ChannelInfo;
  preselectedAgentId?: number;
  draftKey: string;
}

export const ChannelUpdateForm: FC<ChannelUpdateFormProps> = ({
  channel,
  preselectedAgentId,
  draftKey,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation', { keyPrefix: 'channels.form' });
  const { baseFields } = useFormSchemaUpdate();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId?: string }>();
  const normalizedOrganizationId =
    organizationId && Number.isFinite(Number(organizationId)) ? Number(organizationId) : null;
  const channelIntegrationsRoute = normalizedOrganizationId
    ? buildOrganizationRoute(
        Routes.organizationScope.channelsIntegrations,
        normalizedOrganizationId,
        {
          channelId: channel.id,
        },
      )
    : Routes.channels.integrations.replace(':channelId', String(channel.id));

  const { updateChannel, isLoading } = useUpdateChannel();
  const isTelegram = channel.type === 'telegram_bot' || channel.type === 'telegram';
  const isVk = channel.type === 'vk_bot';
  const isWidget = channel.type === 'widget';
  const isMax = channel.type === 'max_bot';
  const isAvito = channel.type === 'avito';
  const savedDraft = useAppSelector(channelFormDraftsSelectors.selectDraftByKey(draftKey)) as
    | Partial<ChannelUpdateFormValues>
    | undefined;
  const initialValues = useMemo<ChannelUpdateFormValues>(
    () => ({
      name: savedDraft?.name ?? channel.name ?? '',
      description: savedDraft?.description ?? channel.description ?? '',
      isActive: savedDraft?.isActive ?? channel.is_active ?? true,
      agentId:
        preselectedAgentId ??
        savedDraft?.agentId ??
        channel.agent_id ??
        (undefined as unknown as number),
      token:
        savedDraft?.token ??
        channel.tg_bot_config?.token ??
        channel.vk_bot_config?.token ??
        channel.max_bot_config?.token ??
        '',
      startMessage:
        savedDraft?.startMessage ??
        channel.tg_bot_config?.start_message ??
        channel.max_bot_config?.start_message ??
        '',
      host: savedDraft?.host ?? channel.widget_config?.host ?? '',
      presetId:
        savedDraft && 'presetId' in savedDraft
          ? savedDraft.presetId
          : (channel.widget_config?.settings_id ?? null),
      clientId: savedDraft?.clientId ?? channel.avito_config?.client_id ?? '',
      clientSecret: savedDraft?.clientSecret ?? channel.avito_config?.client_secret ?? '',
    }),
    [channel, preselectedAgentId, savedDraft],
  );
  const script = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const { protocol, host } = window.location;
    return `<script src="${protocol}//${host}/api/widget/script.js" defer="" data-channel_id="${channel.id}"></script>`;
  }, [channel.id]);
  const handleCopyScript = () => {
    if (!script) return;
    copyToClipboard(script);
  };
  const scriptPopover = (
    <Flex vertical gap={12} className={styles.scriptPopover}>
      <Text variant="md" fontWeight={500}>
        {t('scriptPopoverTitle')}
      </Text>
      <Text variant="sm">{t('scriptPopoverIntro')}</Text>
      <Text variant="md" fontWeight={500}>
        {t('scriptPopoverStep1Title')}
      </Text>

      <Text variant="sm">{t('scriptPopoverStep1Text')}</Text>
      <Text variant="md" fontWeight={500}>
        {t('scriptPopoverStep2Title')}
      </Text>
      <Text variant="sm" fontWeight={500}>
        {t('scriptPopoverStep2TextBuilderTitle')}
      </Text>
      <Text variant="sm" className={styles.scriptDesc}>
        {t('scriptPopoverStep2TextBuilder')}
      </Text>

      <Text variant="sm" fontWeight={500}>
        {t('scriptPopoverStep2TextDevTitle')}
      </Text>
      <Text variant="sm" className={styles.scriptDesc}>
        {t('scriptPopoverStep2TextDev')}
      </Text>

      <Text variant="sm" fontWeight={500}>
        {t('scriptPopoverStep2TextManualTitle')}
      </Text>
      <Text variant="sm" className={styles.scriptDesc}>
        {t('scriptPopoverStep2TextManual')}
      </Text>
    </Flex>
  );
  const tokenHelpContent = (
    <Flex vertical gap={8} style={{ maxWidth: 320 }}>
      <Text variant="sm" fontWeight={600}>
        {t('tokenHelpTitle')}
      </Text>
      {isTelegram && (
        <>
          <Text variant="xs">{t('tokenHelpTelegramStep1')}</Text>
          <Text variant="xs">{t('tokenHelpTelegramStep2')}</Text>
          <Text variant="xs">{t('tokenHelpTelegramStep3')}</Text>
        </>
      )}
      {isVk && (
        <>
          <Text variant="xs">{t('tokenHelpVkStep1')}</Text>
          <Text variant="xs">{t('tokenHelpVkStep2')}</Text>
          <Text variant="xs">{t('tokenHelpVkStep3')}</Text>
        </>
      )}
      {isMax && (
        <>
          <Text variant="xs">{t('tokenHelpMaxStep1')}</Text>
          <Text variant="xs">{t('tokenHelpMaxStep2')}</Text>
          <Text variant="xs">{t('tokenHelpMaxStep3')}</Text>
        </>
      )}
    </Flex>
  );

  const {
    handleSubmit,
    control,
    watch,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<ChannelUpdateFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(baseFields),
    defaultValues: initialValues,
  });

  useEffect(() => {
    const subscription = watch((values) => {
      dispatch(
        channelFormDraftsActions.upsertDraft({
          key: draftKey,
          values: values as Record<string, unknown>,
        }),
      );
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch, draftKey, watch]);

  const handleSubmitForm = async (values: ChannelUpdateFormValues) => {
    const submitValues = preselectedAgentId ? { ...values, agentId: preselectedAgentId } : values;
    await updateChannel(channel.id, channel.type, submitValues);
    dispatch(channelFormDraftsActions.removeDraft(draftKey));
    reset(submitValues);

    if (preselectedAgentId) {
      navigate(generatePath(Routes.aiAgents.agent, { agentId: preselectedAgentId }));
    }
  };

  const handleCancel = () => {
    dispatch(channelFormDraftsActions.removeDraft(draftKey));
    reset(initialValues);
    if (preselectedAgentId) {
      navigate(generatePath(Routes.aiAgents.agent, { agentId: preselectedAgentId }));
      return;
    }

    navigate(Routes.channels.root);
  };

  return (
    <Form
      onChange={() => clearErrors('root')}
      onFinish={handleSubmit(handleSubmitForm)}
      className={styles.form}
      layout="vertical"
    >
      {isWidget && (
        <Flex vertical className={styles.section}>
          <Flex vertical className={styles.sectionHeader}>
            <Text variant="md" fontWeight={500}>
              {t('scriptTitle')}
            </Text>

            <Text variant="sm" fontWeight={400} className={styles.sectionDescription}>
              {t('scriptDescription')}
            </Text>
          </Flex>
          <Flex gap={12} align="center" className={styles.scriptRow}>
            <Input readOnly value={script} size="large" className={styles.scriptInput} />

            <Button
              variant="text"
              size="large"
              onClick={handleCopyScript}
              disabled={!script}
              className={styles.scriptButton}
            >
              {t('copyScript')}
            </Button>
          </Flex>
          <Popover content={scriptPopover} trigger="hover" placement="bottomLeft">
            <Text variant="sm" className={styles.scriptInstruction}>
              {t('scriptInstruction')}
            </Text>
          </Popover>
        </Flex>
      )}

      {(isTelegram || isVk || isMax) && (
        <Flex vertical className={styles.section}>
          <Flex vertical className={styles.sectionHeader}>
            <Text variant="md" fontWeight={600}>
              {t('tokenTitle')}
            </Text>
            <Text variant="xs" className={styles.sectionDescription}>
              {isTelegram ? t('tokenHintTelegram') : isMax ? t('tokenHintMax') : t('tokenHintVk')}
            </Text>
          </Flex>
          <Form.Item
            name="token"
            help={errors.token?.message}
            validateStatus={errors.token ? 'error' : ''}
          >
            <Controller
              control={control}
              name="token"
              render={({ field }) => (
                <Input.Password
                  {...field}
                  disabled={isSubmitting || isLoading}
                  placeholder={t('tokenPlaceholder')}
                  className={styles.input}
                />
              )}
            />
          </Form.Item>
          <Popover content={tokenHelpContent} trigger="hover" placement="bottomLeft">
            <Text variant="xs" className={styles.helpLink}>
              {isTelegram
                ? t('tokenHelpLinkTelegram')
                : isMax
                  ? t('tokenHelpLinkMax')
                  : t('tokenHelpLinkVk')}
            </Text>
          </Popover>
        </Flex>
      )}

      {isAvito && (
        <>
          <Flex vertical className={styles.section}>
            <Flex vertical className={styles.sectionHeader}>
              <Text variant="md" fontWeight={600}>
                {t('clientIdTitle')}
              </Text>
              <Text variant="xs" className={styles.sectionDescription}>
                {t('clientIdHint')}
              </Text>
            </Flex>
            <Form.Item
              name="clientId"
              help={errors.clientId?.message}
              validateStatus={errors.clientId ? 'error' : ''}
            >
              <Controller
                control={control}
                name="clientId"
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting || isLoading}
                    placeholder={t('clientIdPlaceholder')}
                    className={styles.input}
                  />
                )}
              />
            </Form.Item>
          </Flex>

          <Flex vertical className={styles.section}>
            <Flex vertical className={styles.sectionHeader}>
              <Text variant="md" fontWeight={600}>
                {t('clientSecretTitle')}
              </Text>
              <Text variant="xs" className={styles.sectionDescription}>
                {t('clientSecretHint')}
              </Text>
            </Flex>
            <Form.Item
              name="clientSecret"
              help={errors.clientSecret?.message}
              validateStatus={errors.clientSecret ? 'error' : ''}
            >
              <Controller
                control={control}
                name="clientSecret"
                render={({ field }) => (
                  <Input.Password
                    {...field}
                    disabled={isSubmitting || isLoading}
                    placeholder={t('clientSecretPlaceholder')}
                    className={styles.input}
                  />
                )}
              />
            </Form.Item>
          </Flex>
        </>
      )}

      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={500}>
            {t('nameTitle')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('nameHint')}
          </Text>
        </Flex>
        <Form.Item
          name="name"
          help={errors.name?.message}
          validateStatus={errors.name ? 'error' : ''}
        >
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <Input
                {...field}
                showCount
                maxLength={CHANNELS_MAX_FORM_LIMIT.name}
                disabled={isSubmitting || isLoading}
                placeholder={t('namePlaceholder')}
                className={styles.input}
              />
            )}
          />
        </Form.Item>
      </Flex>

      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={500}>
            {t('description')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('descriptionHint')}
          </Text>
        </Flex>
        <Form.Item
          name="description"
          help={errors.description?.message}
          validateStatus={errors.description ? 'error' : ''}
        >
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <Input
                {...field}
                showCount
                maxLength={CHANNELS_MAX_FORM_LIMIT.description}
                disabled={isSubmitting || isLoading}
                placeholder={t('descriptionPlaceholder')}
                className={styles.input}
              />
            )}
          />
        </Form.Item>
      </Flex>

      {isWidget && (
        <>
          <Flex vertical className={styles.section}>
            <Flex vertical className={styles.sectionHeader}>
              <Text variant="md" fontWeight={600}>
                {t('hostTitle')}
              </Text>
              <Text variant="xs" className={styles.sectionDescription}>
                {t('hostHint')}
              </Text>
            </Flex>
            <Form.Item
              name="host"
              help={errors.host?.message}
              validateStatus={errors.host ? 'error' : ''}
            >
              <Controller
                control={control}
                name="host"
                render={({ field }) => (
                  <Input
                    {...field}
                    disabled={isSubmitting || isLoading}
                    placeholder={t('hostPlaceholder')}
                    className={styles.input}
                  />
                )}
              />
            </Form.Item>
          </Flex>

          <Flex vertical className={styles.section}>
            <Flex vertical className={styles.sectionHeader}>
              <Text variant="md" fontWeight={600}>
                {t('presetTitle')}
              </Text>
              <Text variant="xs" className={styles.sectionDescription}>
                {t('presetHint')}
              </Text>
            </Flex>
            <Form.Item name="presetId">
              <Controller
                control={control}
                name="presetId"
                render={({ field }) => (
                  <PresetSelector
                    {...field}
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? null)}
                    disabled={isSubmitting || isLoading}
                    placeholder={t('presetPlaceholder')}
                    className={styles.selector}
                  />
                )}
              />
            </Form.Item>
          </Flex>
        </>
      )}

      {!preselectedAgentId && (
        <Flex vertical className={styles.section}>
          <Flex vertical className={styles.sectionHeader}>
            <Text variant="md" fontWeight={500}>
              {t('agentTitle')}
            </Text>
            <Text variant="xs" className={styles.sectionDescription}>
              {t('agentHintChannel')}
            </Text>
          </Flex>
          <Form.Item
            name="agentId"
            help={errors.agentId?.message}
            validateStatus={errors.agentId ? 'error' : ''}
          >
            <Controller
              control={control}
              name="agentId"
              rules={{ required: t('common.formErrors.required') as string }}
              render={({ field }) => (
                <AgentsSelector
                  {...field}
                  value={field.value}
                  placeholder={t('agentPlaceholder')}
                  onChange={(value) => field.onChange(value)}
                  className={styles.selector}
                />
              )}
            />
          </Form.Item>
        </Flex>
      )}

      {(isTelegram || isMax) && (
        <Flex vertical className={styles.section}>
          <Flex vertical className={styles.sectionHeader}>
            <Text variant="md" fontWeight={600}>
              {t('startMessageTitle')}
            </Text>
            <Text variant="xs" className={styles.sectionDescription}>
              {t('startMessageHint')}
            </Text>
          </Flex>
          <Form.Item
            name="startMessage"
            help={errors.startMessage?.message}
            validateStatus={errors.startMessage ? 'error' : ''}
          >
            <Controller
              control={control}
              name="startMessage"
              render={({ field }) => (
                <Input
                  {...field}
                  showCount
                  maxLength={CHANNELS_MAX_FORM_LIMIT.start_message}
                  disabled={isSubmitting || isLoading}
                  placeholder={t('startMessagePlaceholder')}
                  className={styles.input}
                />
              )}
            />
          </Form.Item>
        </Flex>
      )}

      <ChannelIntegrationsSection
        channelId={channel.id}
        onCreateIntegration={() => {
          navigate(`${channelIntegrationsRoute}?integrationId=new`);
        }}
        onEditIntegration={(integrationId) => {
          navigate(`${channelIntegrationsRoute}?integrationId=${integrationId}`);
        }}
      />

      <Flex vertical className={styles.section}>
        <Flex vertical className={styles.sectionHeader}>
          <Text variant="md" fontWeight={500}>
            {t('statusTitle')}
          </Text>
          <Text variant="xs" className={styles.sectionDescription}>
            {t('statusHint')}
          </Text>
        </Flex>
        <Form.Item style={{ marginBottom: 0 }}>
          <Controller
            control={control}
            name="isActive"
            render={({ field }) => (
              <Flex className={styles.switchRow}>
                <Switch
                  checked={field.value}
                  onChange={(checked) => field.onChange(checked)}
                  disabled={isSubmitting || isLoading}
                />
                <Text variant="sm">{field.value ? t('active') : t('inactive')}</Text>
              </Flex>
            )}
          />
        </Form.Item>
      </Flex>

      <Flex className={styles.actions}>
        <Button
          variant="text"
          onClick={handleCancel}
          className={styles.buttonGhost}
          disabled={isSubmitting || isLoading}
        >
          {t('cancel')}
        </Button>

        <Form.Item className={styles.buttonPrimary}>
          <Button
            className={styles.buttonPrimary}
            type="primary"
            htmlType="submit"
            loading={isSubmitting || isLoading}
            disabled={!isValid || isSubmitting || isLoading || !isDirty}
          >
            {t('submitSave')}
          </Button>
        </Form.Item>
      </Flex>
    </Form>
  );
};
